/**
 * AuthContext – Auth0-basierter Kontext für Admin/Developer-User.
 * Muss innerhalb von Auth0ProviderWithNavigate liegen (in App.js).
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { setAuthToken } from "../utils/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    getAccessTokenSilently,
    logout: auth0Logout,
    loginWithRedirect,
  } = useAuth0();

  // undefined = noch am Laden, null = nicht eingeloggt, object = eingeloggt
  const [user, setUser] = useState(undefined);
  const [token, setToken] = useState(null);

  const refreshToken = useCallback(async () => {
    try {
      const t = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || "https://api.huwnd.de",
        },
      });
      setToken(t);
      setAuthToken(t);
      return t;
    } catch {
      return null;
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (auth0Loading) return;

    if (!isAuthenticated) {
      setUser(null);
      setToken(null);
      setAuthToken(null);
      return;
    }

    let cancelled = false;

    const syncUser = async () => {
      try {
        const t = await refreshToken();
        if (!t || cancelled) return;

        // auth0User kommt vom ID-Token (hat Name, Email, Picture)
        // Access-Token enthält diese Daten oft NICHT → als Fallback mitsenden
        const res = await axios.post(
          `${BACKEND_URL}/api/me/sync`,
          {
            name: auth0User?.name || auth0User?.nickname,
            email: auth0User?.email,
            picture: auth0User?.picture,
          },
          { headers: { Authorization: `Bearer ${t}` } }
        );

        if (!cancelled) {
          setUser({
            ...res.data,
            picture: auth0User?.picture || res.data.avatar_url,
          });
        }
      } catch {
        if (!cancelled) setUser(null);
      }
    };

    syncUser();
    return () => { cancelled = true; };
  }, [isAuthenticated, auth0Loading, refreshToken, auth0User]);

  const logout = () => {
    setUser(null);
    setToken(null);
    setAuthToken(null);
    auth0Logout({ logoutParams: { returnTo: window.location.origin + "/login" } });
  };

  const login = () => {
    loginWithRedirect({ appState: { returnTo: "/admin/dashboard" } });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
